import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import Layout from '../components/Layout';
import {
  BarChart3, Package, Clock, FileText, Box, TrendingUp,
  Download, RefreshCw, Filter, Calendar, ChevronDown
} from 'lucide-react';

const API_URL = process.env.REACT_APP_BACKEND_URL;
const ZOBOX_BLUE = '#2b3380';

function WarehouseReports({ user, onLogout }) {
  const navigate = useNavigate();
  const [activeReport, setActiveReport] = useState('inventory');
  const [loading, setLoading] = useState(true);
  const [reportData, setReportData] = useState(null);
  const [filters, setFilters] = useState({
    category: '',
    brand: '',
    dateFrom: '',
    dateTo: '',
    transactionType: ''
  });

  useEffect(() => {
    loadReport(activeReport);
  }, [activeReport]);

  const loadReport = async (reportType) => {
    setLoading(true);
    try {
      const token = localStorage.getItem('token');
      let endpoint = '';
      
      switch (reportType) {
        case 'inventory':
          endpoint = `/api/warehouse/reports/inventory?category=${filters.category}&brand=${filters.brand}`;
          break;
        case 'aging':
          endpoint = '/api/warehouse/reports/aging';
          break;
        case 'transactions':
          endpoint = `/api/warehouse/reports/transactions?transaction_type=${filters.transactionType}&date_from=${filters.dateFrom}&date_to=${filters.dateTo}`;
          break;
        case 'bin-utilization':
          endpoint = '/api/warehouse/reports/bin-utilization';
          break;
        default:
          endpoint = '/api/warehouse/reports/inventory';
      }
      
      const response = await fetch(`${API_URL}${endpoint}`, {
        headers: { Authorization: `Bearer ${token}` }
      });
      const data = await response.json();
      
      if (data.success) {
        setReportData(data);
      }
    } catch (error) {
      console.error('Error loading report:', error);
    } finally {
      setLoading(false);
    }
  };

  const reportTabs = [
    { id: 'inventory', label: 'Inventory', icon: Package },
    { id: 'aging', label: 'Device Aging', icon: Clock },
    { id: 'transactions', label: 'Transactions', icon: FileText },
    { id: 'bin-utilization', label: 'Bin Utilization', icon: Box },
  ];

  const renderInventoryReport = () => (
    <div className="space-y-6">
      {/* Summary Cards */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total Devices</p>
          <p className="text-2xl font-bold text-[#2b3380]">{reportData?.total_devices || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total Value</p>
          <p className="text-2xl font-bold text-green-600">₹{(reportData?.total_value || 0).toLocaleString()}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Categories</p>
          <p className="text-2xl font-bold text-gray-800">{Object.keys(reportData?.by_category || {}).length}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Status Types</p>
          <p className="text-2xl font-bold text-gray-800">{Object.keys(reportData?.by_status || {}).length}</p>
        </div>
      </div>

      {/* By Category */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-800 mb-4">By Category</h3>
        <div className="space-y-3">
          {Object.entries(reportData?.by_category || {}).map(([cat, data]) => (
            <div key={cat} className="flex items-center justify-between p-3 bg-gray-50 rounded-lg">
              <div className="flex items-center gap-3">
                <Package className="w-5 h-5 text-[#2b3380]" />
                <span className="font-medium">{cat}</span>
              </div>
              <div className="text-right">
                <p className="font-semibold text-gray-800">{data.count} devices</p>
                <p className="text-sm text-gray-500">₹{data.value?.toLocaleString()}</p>
              </div>
            </div>
          ))}
        </div>
      </div>

      {/* By Status */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-800 mb-4">By Status</h3>
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {Object.entries(reportData?.by_status || {}).map(([status, count]) => (
            <div key={status} className="p-3 bg-gray-50 rounded-lg text-center">
              <p className="text-xs text-gray-500 mb-1">{status.replace(/_/g, ' ')}</p>
              <p className="text-xl font-bold text-[#2b3380]">{count}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Detailed Inventory */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-800">Detailed Inventory</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Category</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Brand</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Model</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Count</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Value</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {(reportData?.inventory || []).slice(0, 20).map((item, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm">{item._id?.category || '-'}</td>
                  <td className="px-4 py-3 text-sm font-medium">{item._id?.brand || '-'}</td>
                  <td className="px-4 py-3 text-sm">{item._id?.model || '-'}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className="px-2 py-1 bg-blue-100 text-blue-700 text-xs rounded-full">
                      {item._id?.status?.replace(/_/g, ' ') || '-'}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-right font-semibold">{item.count}</td>
                  <td className="px-4 py-3 text-sm text-right">₹{item.total_value?.toLocaleString()}</td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderAgingReport = () => (
    <div className="space-y-6">
      {/* Aging Buckets */}
      <div className="bg-white rounded-xl border border-gray-200 p-6">
        <h3 className="font-semibold text-gray-800 mb-4">Device Aging Distribution</h3>
        <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-6 gap-4">
          {(reportData?.aging_buckets || []).map((bucket, idx) => {
            const colors = ['bg-green-100 text-green-700', 'bg-blue-100 text-blue-700', 'bg-yellow-100 text-yellow-700', 'bg-orange-100 text-orange-700', 'bg-red-100 text-red-700', 'bg-purple-100 text-purple-700'];
            return (
              <div key={bucket.bucket} className={`p-4 rounded-xl ${colors[idx] || colors[0]}`}>
                <p className="text-sm font-medium mb-1">{bucket.bucket}</p>
                <p className="text-3xl font-bold">{bucket.count}</p>
                <p className="text-xs opacity-75">devices</p>
              </div>
            );
          })}
        </div>
      </div>

      {/* Oldest Devices */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-800">Oldest Devices (Need Attention)</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">DID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Brand</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Model</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date Added</th>
                <th className="px-4 py-3 text-right text-xs font-medium text-gray-500 uppercase">Age</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {(reportData?.oldest_devices || []).map((device) => {
                const addedDate = new Date(device.created_at);
                const ageInDays = Math.floor((new Date() - addedDate) / (1000 * 60 * 60 * 24));
                return (
                  <tr key={device.device_id} className="hover:bg-gray-50">
                    <td className="px-4 py-3 text-sm font-mono">{device.device_id}</td>
                    <td className="px-4 py-3 text-sm font-medium">{device.brand}</td>
                    <td className="px-4 py-3 text-sm">{device.model}</td>
                    <td className="px-4 py-3 text-sm">
                      <span className="px-2 py-1 bg-orange-100 text-orange-700 text-xs rounded-full">
                        {device.status?.replace(/_/g, ' ')}
                      </span>
                    </td>
                    <td className="px-4 py-3 text-sm text-gray-500">
                      {addedDate.toLocaleDateString()}
                    </td>
                    <td className="px-4 py-3 text-sm text-right font-semibold text-red-600">
                      {ageInDays} days
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderTransactionsReport = () => (
    <div className="space-y-6">
      {/* Filters */}
      <div className="bg-white rounded-xl border border-gray-200 p-4">
        <div className="flex flex-wrap gap-4">
          <select
            value={filters.transactionType}
            onChange={(e) => {
              setFilters({ ...filters, transactionType: e.target.value });
              setTimeout(() => loadReport('transactions'), 100);
            }}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
          >
            <option value="">All Types</option>
            <option value="GRN">GRN</option>
            <option value="STN">STN</option>
            <option value="SRN">SRN</option>
          </select>
          <input
            type="date"
            value={filters.dateFrom}
            onChange={(e) => setFilters({ ...filters, dateFrom: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            placeholder="From Date"
          />
          <input
            type="date"
            value={filters.dateTo}
            onChange={(e) => setFilters({ ...filters, dateTo: e.target.value })}
            className="px-3 py-2 border border-gray-300 rounded-lg text-sm"
            placeholder="To Date"
          />
          <button
            onClick={() => loadReport('transactions')}
            className="px-4 py-2 bg-[#2b3380] text-white rounded-lg text-sm flex items-center gap-2"
          >
            <Filter className="w-4 h-4" />
            Apply
          </button>
        </div>
      </div>

      {/* Transactions List */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
          <h3 className="font-semibold text-gray-800">Transaction Log ({reportData?.total || 0})</h3>
          <button className="text-sm text-[#2b3380] flex items-center gap-1">
            <Download className="w-4 h-4" />
            Export
          </button>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Type</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Source/Reference</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Devices</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Date</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {(reportData?.transactions || []).map((txn, idx) => (
                <tr key={idx} className="hover:bg-gray-50">
                  <td className="px-4 py-3">
                    <span className={`px-2 py-1 text-xs font-medium rounded-full ${
                      txn.type === 'GRN' ? 'bg-green-100 text-green-700' :
                      txn.type === 'STN' ? 'bg-blue-100 text-blue-700' :
                      'bg-purple-100 text-purple-700'
                    }`}>
                      {txn.type}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm font-mono">{txn.id}</td>
                  <td className="px-4 py-3 text-sm">
                    {txn.source_type || txn.source || '-'}
                    {txn.source_reference && <span className="text-gray-500"> ({txn.source_reference})</span>}
                  </td>
                  <td className="px-4 py-3 text-sm text-center font-semibold">{txn.device_count}</td>
                  <td className="px-4 py-3 text-sm">
                    <span className={`px-2 py-1 text-xs rounded-full ${
                      txn.status === 'COMPLETED' || txn.status === 'RECEIVED' ? 'bg-green-100 text-green-700' :
                      txn.status === 'DISPATCHED' ? 'bg-blue-100 text-blue-700' :
                      'bg-gray-100 text-gray-700'
                    }`}>
                      {txn.status}
                    </span>
                  </td>
                  <td className="px-4 py-3 text-sm text-gray-500">
                    {new Date(txn.created_at).toLocaleString()}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  const renderBinUtilizationReport = () => (
    <div className="space-y-6">
      {/* Summary */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total Bins</p>
          <p className="text-2xl font-bold text-[#2b3380]">{reportData?.summary?.total_bins || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Bins In Use</p>
          <p className="text-2xl font-bold text-green-600">{reportData?.summary?.bins_in_use || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Empty Bins</p>
          <p className="text-2xl font-bold text-gray-400">{reportData?.summary?.empty_bins || 0}</p>
        </div>
        <div className="bg-white rounded-xl border border-gray-200 p-4">
          <p className="text-sm text-gray-500">Total Devices</p>
          <p className="text-2xl font-bold text-gray-800">{reportData?.summary?.total_devices || 0}</p>
        </div>
      </div>

      {/* Utilization Table */}
      <div className="bg-white rounded-xl border border-gray-200 overflow-hidden">
        <div className="px-6 py-4 border-b border-gray-200">
          <h3 className="font-semibold text-gray-800">Bin Utilization</h3>
        </div>
        <div className="overflow-x-auto">
          <table className="w-full">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Bin ID</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Location</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Assigned Model</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Devices</th>
                <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase">Capacity</th>
                <th className="px-4 py-3 text-left text-xs font-medium text-gray-500 uppercase">Utilization</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-200">
              {(reportData?.utilization || []).map((bin) => (
                <tr key={bin.bin_id} className="hover:bg-gray-50">
                  <td className="px-4 py-3 text-sm font-mono">{bin.bin_id}</td>
                  <td className="px-4 py-3 text-sm">{bin.location}</td>
                  <td className="px-4 py-3 text-sm">{bin.assigned_model || '-'}</td>
                  <td className="px-4 py-3 text-sm text-center font-semibold">{bin.device_count}</td>
                  <td className="px-4 py-3 text-sm text-center">{bin.capacity}</td>
                  <td className="px-4 py-3">
                    <div className="flex items-center gap-2">
                      <div className="flex-1 h-2 bg-gray-200 rounded-full overflow-hidden">
                        <div 
                          className={`h-full rounded-full ${
                            bin.utilization_percent > 80 ? 'bg-red-500' :
                            bin.utilization_percent > 50 ? 'bg-yellow-500' :
                            'bg-green-500'
                          }`}
                          style={{ width: `${Math.min(bin.utilization_percent, 100)}%` }}
                        />
                      </div>
                      <span className="text-sm font-medium w-12 text-right">{bin.utilization_percent}%</span>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      </div>
    </div>
  );

  return (
    <Layout user={user} onLogout={onLogout}>
      <div className="min-h-screen bg-gray-50">
        {/* Header */}
        <div className="bg-white border-b border-gray-200 px-6 py-4">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-xl font-bold" style={{ color: ZOBOX_BLUE }}>Warehouse Reports</h1>
              <p className="text-sm text-gray-500">Analytics and insights</p>
            </div>
            <button
              onClick={() => loadReport(activeReport)}
              className="flex items-center gap-2 px-4 py-2 border border-gray-300 rounded-lg hover:bg-gray-50"
            >
              <RefreshCw className={`w-4 h-4 ${loading ? 'animate-spin' : ''}`} />
              Refresh
            </button>
          </div>
        </div>

        {/* Report Tabs */}
        <div className="bg-white border-b border-gray-200 px-6">
          <div className="flex gap-6 overflow-x-auto">
            {reportTabs.map((tab) => {
              const Icon = tab.icon;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveReport(tab.id)}
                  className={`flex items-center gap-2 py-4 border-b-2 transition-colors whitespace-nowrap ${
                    activeReport === tab.id
                      ? 'border-[#2b3380] text-[#2b3380]'
                      : 'border-transparent text-gray-500 hover:text-gray-700'
                  }`}
                  data-testid={`report-tab-${tab.id}`}
                >
                  <Icon className="w-4 h-4" />
                  {tab.label}
                </button>
              );
            })}
          </div>
        </div>

        {/* Report Content */}
        <div className="p-6">
          {loading ? (
            <div className="flex items-center justify-center py-20">
              <div className="animate-spin rounded-full h-12 w-12 border-4 border-[#2b3380] border-t-transparent"></div>
            </div>
          ) : (
            <>
              {activeReport === 'inventory' && renderInventoryReport()}
              {activeReport === 'aging' && renderAgingReport()}
              {activeReport === 'transactions' && renderTransactionsReport()}
              {activeReport === 'bin-utilization' && renderBinUtilizationReport()}
            </>
          )}
        </div>
      </div>
    </Layout>
  );
}

export default WarehouseReports;
